import { useState, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import {
  useGetTrainerInvite,
  useAcceptTrainerInvite,
  completeStripeConnect,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, User, Lock, Briefcase, CreditCard, ArrowRight, ArrowLeft, Loader2, XCircle } from "lucide-react";

type Step = "password" | "profile" | "stripe" | "complete";

export default function TrainerOnboard() {
  const [, params] = useRoute("/trainer-onboard/:token");
  const token = params?.token ?? "";
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const searchParams = new URLSearchParams(window.location.search);
  const stripeStatus = searchParams.get("stripe");

  const { data: inviteDetails, isLoading, error } = useGetTrainerInvite(token, {
    query: { enabled: !!token, retry: false },
  });
  const acceptInvite = useAcceptTrainerInvite();

  const [step, setStep] = useState<Step>("password");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [bio, setBio] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [certifications, setCertifications] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [stripeLinkUrl, setStripeLinkUrl] = useState<string | null>(null);
  const [stripeErrorMsg, setStripeErrorMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (stripeStatus === "complete") {
      setStep("complete");
    }
  }, [stripeStatus]);

  useEffect(() => {
    if (stripeStatus === "refresh" && token) {
      setStep("stripe");
    }
  }, [stripeStatus, token]);

  const handleAccept = async () => {
    if (!password || password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await acceptInvite.mutateAsync({
        token,
        data: {
          password,
          bio: bio || undefined,
          specialties: specialties ? specialties.split(",").map(s => s.trim()).filter(Boolean) : undefined,
          certifications: certifications ? certifications.split(",").map(s => s.trim()).filter(Boolean) : undefined,
          yearsExperience: yearsExperience ? parseInt(yearsExperience) : undefined,
        },
      });

      if (result.stripeAccountLinkUrl) {
        setStripeLinkUrl(result.stripeAccountLinkUrl);
        setStripeErrorMsg(null);
        setStep("stripe");
      } else if (result.stripeError) {
        setStripeErrorMsg(result.stripeError);
        setStep("stripe");
      } else {
        setStripeErrorMsg("Stripe setup is not available right now.");
        setStep("stripe");
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to create account";
      toast({ title: msg, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: "password", label: "Account", icon: <Lock className="w-4 h-4" /> },
    { key: "profile", label: "Profile", icon: <User className="w-4 h-4" /> },
    { key: "stripe", label: "Payouts", icon: <CreditCard className="w-4 h-4" /> },
    { key: "complete", label: "Done", icon: <CheckCircle className="w-4 h-4" /> },
  ];

  const stepIndex = steps.findIndex(s => s.key === step);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !inviteDetails) {
    const errorMsg = (error as any)?.message || "This invite link is invalid, expired, or has already been used.";
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="glass-panel border-white/10 max-w-md w-full">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-4">
            <XCircle className="w-12 h-12 text-red-400" />
            <h2 className="text-xl font-display font-bold text-white">Invalid Invite</h2>
            <p className="text-muted-foreground">{errorMsg}</p>
            <Button variant="outline" onClick={() => setLocation("/login")}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-primary mb-2">OPEN MAT</h1>
          <p className="text-muted-foreground mb-4">
            {inviteDetails.inviterName ? (
              <><span className="text-white font-semibold">{inviteDetails.inviterName}</span> at </>
            ) : null}
            <span className="text-white font-semibold">{inviteDetails.gymName}</span> invited you to join OPEN MAT — the platform that handles scheduling, payments, and payouts for private training sessions.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-xs text-muted-foreground">
            <span className="bg-white/5 border border-white/10 rounded-full px-3 py-1">💰 Guaranteed payments</span>
            <span className="bg-white/5 border border-white/10 rounded-full px-3 py-1">📅 Automated scheduling</span>
            <span className="bg-white/5 border border-white/10 rounded-full px-3 py-1">🚫 Fewer no-shows</span>
            <span className="bg-white/5 border border-white/10 rounded-full px-3 py-1">⭐ Build your reputation</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                i <= stepIndex
                  ? "bg-primary/20 text-primary border border-primary/30"
                  : "bg-white/5 text-muted-foreground border border-white/10"
              }`}>
                {s.icon}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-6 h-px mx-1 ${i < stepIndex ? "bg-primary/50" : "bg-white/10"}`} />
              )}
            </div>
          ))}
        </div>

        <Card className="glass-panel border-white/10">
          {step === "password" && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase tracking-wide">Create Your Account</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-1">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="text-white font-medium">{inviteDetails.email}</p>
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    className="glass-panel border-white/10"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm Password *</Label>
                  <Input
                    className="glass-panel border-white/10"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={() => {
                    if (!password || password.length < 6) {
                      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
                      return;
                    }
                    if (password !== confirmPassword) {
                      toast({ title: "Passwords do not match", variant: "destructive" });
                      return;
                    }
                    setStep("profile");
                  }} className="gap-2">
                    Next <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {step === "profile" && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase tracking-wide">Your Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Bio</Label>
                  <textarea
                    className="w-full min-h-[100px] glass-panel border border-white/10 rounded-md px-3 py-2 text-sm bg-transparent text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    placeholder="Tell students about yourself, your teaching style, and experience..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Specialties</Label>
                  <Input
                    className="glass-panel border-white/10"
                    value={specialties}
                    onChange={e => setSpecialties(e.target.value)}
                    placeholder="BJJ, Muay Thai, Wrestling (comma-separated)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Certifications</Label>
                  <Input
                    className="glass-panel border-white/10"
                    value={certifications}
                    onChange={e => setCertifications(e.target.value)}
                    placeholder="Black Belt BJJ, Certified PT (comma-separated)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input
                    className="glass-panel border-white/10"
                    type="number"
                    value={yearsExperience}
                    onChange={e => setYearsExperience(e.target.value)}
                    placeholder="5"
                  />
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep("password")} className="gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </Button>
                  <Button onClick={handleAccept} disabled={isSubmitting} className="gap-2">
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Creating Account...</>
                    ) : (
                      <>Create Account & Set Up Payouts <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {step === "stripe" && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase tracking-wide">Connect Your Bank Account</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  To receive payouts from your sessions, you need to connect your bank account through Stripe. This is a secure process handled entirely by Stripe.
                </p>
                <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
                  <p className="text-sm text-primary font-medium">Your trainer account has been created successfully!</p>
                  <p className="text-xs text-muted-foreground mt-1">Complete the Stripe setup to start receiving payouts for your sessions.</p>
                </div>
                {stripeErrorMsg && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p className="text-sm text-red-400 font-medium">Payment setup encountered an issue</p>
                    <p className="text-xs text-muted-foreground mt-1">{stripeErrorMsg}</p>
                  </div>
                )}
                {stripeLinkUrl ? (
                  <div className="flex flex-col gap-3">
                    <Button
                      className="w-full gap-2"
                      onClick={() => window.location.href = stripeLinkUrl}
                    >
                      <CreditCard className="w-4 h-4" /> Connect with Stripe
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-xs text-muted-foreground"
                      onClick={() => setStep("complete")}
                    >
                      Skip for now — you can set this up later from your Payouts page
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="text-sm text-muted-foreground">
                      Don't worry — your trainer account was created. You can connect your bank account anytime from the <span className="text-white font-medium">Payouts</span> page after logging in.
                    </p>
                    <Button onClick={() => setStep("complete")} className="w-full gap-2">
                      Continue <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </>
          )}

          {step === "complete" && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase tracking-wide">You're All Set!</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col items-center text-center py-4 gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-lg">Welcome to {inviteDetails.gymName}!</p>
                    <p className="text-muted-foreground mt-1">
                      Your trainer account is ready. Log in to start managing your availability and sessions.
                    </p>
                  </div>
                </div>
                <Button className="w-full gap-2" onClick={() => setLocation("/login")}>
                  Go to Login <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
